// components/Stats.tsx
import React from 'react';
import { getStats } from '../utils/stats';
import { GameStats, GuessDistribution } from '../types/stats';

interface StatsProps {
  gameResult?: {
    isWin: boolean;
    correctWord: string;
  };
  onClose?: () => void;
}

export const Stats: React.FC<StatsProps> = ({ gameResult, onClose }) => {
  const stats: GameStats = getStats();
  
  // Calculate maxDistribution here, outside the map function
  const distributionValues: number[] = [
    stats.guessDistribution[1],
    stats.guessDistribution[2],
    stats.guessDistribution[3],
    stats.guessDistribution[4],
    stats.guessDistribution[5],
    stats.guessDistribution.fail,
  ];
  const maxDistribution = Math.max(...distributionValues);
  const total = distributionValues.reduce((sum, count) => sum + count, 0);

  const weightedSum = distributionValues.reduce(
    (sum, count, index) => sum + count * (index + 1),
    0
  );

  const averagePosition = total === 0 ? 0 : weightedSum / total;
  const averageDistribution = averagePosition.toFixed(2);
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-xl p-6 max-w-md w-full mx-4 border border-gray-700">
        {/* Header with close button */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-white">
            {gameResult ? (gameResult.isWin ? 'Great Job!' : 'Game Over') : 'Statistics'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-300 hover:text-white text-2xl"
          >
            ×
          </button>
        </div>

        {/* Game result message */}
        {gameResult && (
          <div className="text-center mb-6">
            {!gameResult.isWin && (
              <p className="text-white text-lg font-semibold mb-2">
                The word was: <span className="font-bold text-blue-300">{gameResult.correctWord}</span>
              </p>
            )}
            <p className="text-gray-200 font-medium">
              {gameResult.isWin 
                ? 'Congratulations! You guessed the word!' 
                : 'Better luck next time!'
              }
            </p>
          </div>
        )}

        {!stats.gamesPlayed ? (
          <div className="text-gray-400 text-center">No games played yet</div>
        ) : (
          <>
            <div className="grid grid-cols-4 gap-4 text-center text-sm mb-6">
              <div>
                <div className="text-2xl font-bold text-white">{stats.gamesPlayed}</div>
                <div className="text-gray-400">Played</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-white">
                  {Math.round((stats.gamesPlayed > 0 ? (stats.wins / stats.gamesPlayed) * 100 : 0))}%
                </div>
                <div className="text-gray-400">Win %</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-white">{stats.currentStreak}</div>
                <div className="text-gray-400">Current Streak</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-white">{stats.maxStreak}</div>
                <div className="text-gray-400">Max Streak</div>
              </div>
              <div className='col-span-4 mt-4'>
                <div className="text-2xl font-bold text-white">{averageDistribution}</div>
                <div className="text-gray-400">Average Guess</div>
              </div>
            </div>

            {/* Guess Distribution Section */}
            <div className="mt-4">
              <h4 className="text-white font-bold mb-3 text-center">Guess Distribution</h4>
              <div className="space-y-2">
                {[1, 2, 3, 4, 5].map((attempt) => {
                  const count = stats.guessDistribution[attempt as keyof GuessDistribution] as number;
                  const widthPercent = count > 0 && maxDistribution > 0 
                    ? `${Math.max(10, (count / maxDistribution) * 100)}%` 
                    : '10%';
                  
                  return (
                    <div key={attempt} className="flex items-center text-sm">
                      <span className="text-white w-6 mr-2 text-right">{attempt}</span>
                      <div 
                        className="text-white py-1 px-3 rounded text-right transition-all duration-300 min-w-[40px]"
                        style={{
                          width: widthPercent,
                          backgroundColor: count > 0 ? '#16a34a' : '#4b5563'
                        }}
                      >
                        {count}
                      </div>
                    </div>
                  );
                })}
                {/* Fail row */}
                <div key="fail" className="flex items-center text-sm">
                  <span className="text-white w-6 mr-2 text-right">X</span>
                  <div 
                    className="text-white py-1 px-3 rounded text-right transition-all duration-300 min-w-[40px]"
                    style={{
                      width: stats.guessDistribution.fail > 0 && maxDistribution > 0
                        ? `${Math.max(10, (stats.guessDistribution.fail / maxDistribution) * 100)}%` 
                        : '10%',
                      backgroundColor: stats.guessDistribution.fail > 0 ? '#dc2626' : '#4b5563'
                    }}
                  >
                    {stats.guessDistribution.fail}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Play Again button when showing game result */}
        {gameResult && onClose && (
          <div className="flex justify-center mt-6">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700 transition"
            >
              Play Again
            </button>
          </div>
        )}
      </div>
    </div>
  );
};